(() => {
  if (customElements.get('custom-sticky-add-to-cart')) return;

  class CustomStickyAddToCart extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.bar = this.querySelector('.custom-sticky-atc__bar');
      this.variantText = this.querySelector('.custom-sticky-atc__variant');
      this.stickyPriceTarget = this.querySelector('[data-sticky-price]');
      this.buyNowButton = this.querySelector('[data-buy-now]');
      this.stickyButton = this.querySelector('[data-sticky-submit]');
      this.buttonsRow = this.querySelector('[data-sticky-buttons-row]');
      this.soldOutButton = this.querySelector('[data-sticky-sold-out]');
      this.handleSoldOutClick = this.handleSoldOutClick.bind(this);
      this.soldOutButton?.addEventListener('click', this.handleSoldOutClick);
      this.modal = this.querySelector('[data-size-modal]');
      this.modalOptions = this.querySelector('[data-size-options]');
      this.modalSubmit = this.querySelector('[data-modal-submit]');
      this.modalBuyNow = this.querySelector('[data-modal-buy-now]');
      this.modalError = this.querySelector('[data-modal-error]');
      this.stickyFormErrors = Array.from(this.querySelectorAll('[data-sticky-form-error]'));
      this.modalCloseButtons = this.querySelectorAll('[data-modal-close]');
      this.sizeGuideSlot = this.querySelector('[data-size-guide-slot]');
      this.defaultVariantText = this.variantText?.dataset.defaultText || 'Select your size';
      this.rootSection = document.getElementById(`MainProduct-${this.sectionId}`) || document.querySelector(`[data-section="${this.sectionId}"]`);
      this.form = this.rootSection?.querySelector(`#product-form-${this.sectionId}`) || document.getElementById(`product-form-${this.sectionId}`);
      this.formWrapper = this.rootSection?.querySelector('.but-btn-container');
      this.ctaContainer = this.formWrapper?.querySelector('.product-form__buttons');
      this.originalErrorWrapper = this.rootSection?.querySelector('product-form .product-form__error-message-wrapper');
      // This class used to be an inline <script> at the end of this element, so it ran mid-parse and could only
      // see elements earlier in the page. Footer targets always come later, so this list was always empty.
      // Keep that exact behaviour now that the script is deferred: only count elements before this one.
      this.footerTargets = Array.from(
        document.querySelectorAll('.footer-top-section, .shopify-section-group-footer-group, .footer, #shopify-section-footer, footer')
      ).filter((el) => this.isBeforeThis(el));
      this.infoRow = this.querySelector('.custom-sticky-atc-info-row');
      this.pdpDetails = document.querySelector('.pdp-details');
      this.sizeFieldset = this.getSizeFieldset();
      this.isSubmitting = false;
      this.isCtaOutOfView = false;
      this.scrollPastOnly = this.dataset.scrollPastOnly === 'true';
      this.isFooterVisible = false;
      this.footerHideOffset = 24;
      this.footerHideDistance = 220;

      this.isPromoMode = Boolean(this.querySelector('.custom-sticky-atc-promo-link'));

      if (this.isPromoMode) {
        if (!this.bar) return;
        this.handleViewportChange = this.handleViewportChange.bind(this);
        window.addEventListener('scroll', this.handleViewportChange, { passive: true });
        window.addEventListener('resize', this.handleViewportChange);

        const promoLink = this.rootSection?.querySelector('.promo_link') || this.formWrapper?.querySelector('.promo_link');
        const promoTarget = promoLink && this.isBeforeThis(promoLink) ? promoLink : null;
        this.promoObserveTarget = promoTarget || this.rootSection;
        if (this.promoObserveTarget) {
          this.buyObserver = new IntersectionObserver((entries) => {
            this.isCtaOutOfView = this.isTargetOutOfView(entries[0].boundingClientRect);
            this.updateBarVisibility();
          }, { threshold: 0.05 });
          this.buyObserver.observe(this.promoObserveTarget);
        }
        this.setupPdpDetailsObserver();
        this.evaluateVisibility();
        return;
      }

      if (!this.bar || !this.form || !this.formWrapper || !this.ctaContainer) return;

      this.handleStickyClick = this.handleStickyClick.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleDomSync = this.handleDomSync.bind(this);
      this.handleModalSubmit = this.handleModalSubmit.bind(this);
      this.handleEscape = this.handleEscape.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);

      this.handleBuyNowClick = this.handleBuyNowClick.bind(this);
      this.handleModalBuyNowClick = this.handleModalBuyNowClick.bind(this);

      this.stickyButton?.addEventListener('click', this.handleStickyClick);
      this.buyNowButton?.addEventListener('click', this.handleBuyNowClick);
      this.modalBuyNow?.addEventListener('click', this.handleModalBuyNowClick);
      document.addEventListener('custom:variant:changed', this.handleVariantChange);
      this.modalSubmit?.addEventListener('click', this.handleModalSubmit);
      this.modalCloseButtons.forEach((button) => button.addEventListener('click', () => this.closeModal()));
      document.addEventListener('keydown', this.handleEscape);
      window.addEventListener('scroll', this.handleViewportChange, { passive: true });
      window.addEventListener('resize', this.handleViewportChange);

      this.setupObservers();
      this.setupPdpDetailsObserver();
      this.handleDomSync();
    }

    disconnectedCallback() {
      if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
      this.syncFrame = 0;
      this.buyObserver?.disconnect();
      this.priceObserver?.disconnect();
      this.formObserver?.disconnect();
      this.pdpDetailsObserver?.disconnect();
      this.errorObserver?.disconnect();
      this.stickyButton?.removeEventListener('click', this.handleStickyClick);
      this.soldOutButton?.removeEventListener('click', this.handleSoldOutClick);
      this.buyNowButton?.removeEventListener('click', this.handleBuyNowClick);
      this.modalBuyNow?.removeEventListener('click', this.handleModalBuyNowClick);
      this.modalSubmit?.removeEventListener('click', this.handleModalSubmit);
      document.removeEventListener('custom:variant:changed', this.handleVariantChange);
      document.removeEventListener('keydown', this.handleEscape);
      window.removeEventListener('scroll', this.handleViewportChange);
      window.removeEventListener('resize', this.handleViewportChange);
    }

    setupObservers() {
      this.observeCtaTarget();

      const livePrice = document.getElementById(`price-${this.sectionId}`);
      if (livePrice) {
        this.priceObserver = new MutationObserver(() => this.scheduleSync());
        this.priceObserver.observe(livePrice, { childList: true, subtree: true, characterData: true, attributes: true });
      }

      this.formObserver = new MutationObserver(() => this.scheduleSync({ ctaChanged: true }));
      this.formObserver.observe(this.formWrapper, { childList: true, subtree: true, attributes: true });

      if (this.originalErrorWrapper) {
        this.errorObserver = new MutationObserver(() => this.syncFormError());
        this.errorObserver.observe(this.originalErrorWrapper, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['hidden'],
        });
      }
    }

    // A size tap mutates the buy area in many separate batches (price, button, stock message...), and each batch
    // used to run the full sync below, about 13 times per tap. Collect them and run the sync once in the next frame:
    // it reads the final DOM, so the end state is the same.
    scheduleSync({ ctaChanged = false } = {}) {
      if (ctaChanged) this.pendingCtaChange = true;
      if (this.syncFrame) return;
      this.syncFrame = requestAnimationFrame(() => this.flushSync());
    }

    // Runs a pending sync immediately, so a click in the same frame never acts on stale state.
    flushSync() {
      if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
      const hadPending = Boolean(this.syncFrame);
      this.syncFrame = 0;
      if (!hadPending) return;
      if (this.pendingCtaChange) {
        this.pendingCtaChange = false;
        this.sizeFieldset = this.getSizeFieldset();
        this.observeCtaTarget();
      }
      this.handleDomSync();
    }

    isBeforeThis(element) {
      return Boolean(element && element.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_FOLLOWING);
    }

    observeCtaTarget() {
      const target = this.getObservedCtaTarget();
      if (!target) {
        this.buyObserver?.disconnect();
        this.observedCtaTarget = null;
        return;
      }

      // Re-observing the same element only repeats work; evaluateVisibility() below keeps the state current.
      if (target === this.observedCtaTarget && this.buyObserver) {
        this.evaluateVisibility();
        return;
      }

      this.buyObserver?.disconnect();
      this.observedCtaTarget = target;
      this.buyObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;

        this.isCtaOutOfView = this.isTargetOutOfView(entry.boundingClientRect);
        this.updateBarVisibility();
      }, { threshold: 0.05 });

      this.buyObserver.observe(target);
      this.evaluateVisibility();
    }

    handleVariantChange(event) {
      const variant = event.detail?.variant;
      if (!variant) return;

      if (String(variant.id) !== String(this.getCurrentVariantId())) return;
      this.updateVariantText(variant.title);
      this.scheduleSync();
    }

    handleDomSync() {
      this.syncPrice();
      this.syncButtonState();
      this.syncFormError();
      this.syncVariantTextFromDom();
      this.syncModalOptions();
      this.syncOosMessage();
      this.syncSizeNote();
      this.evaluateVisibility();
    }

    setupPdpDetailsObserver() {
      if (!this.infoRow || !this.pdpDetails) return;

      this.pdpDetailsObserver = new IntersectionObserver((entries) => {
        const isInViewport = entries[0].isIntersecting;
        this.infoRow.style.display = isInViewport ? 'none' : 'flex';
      }, { threshold: 0 });

      this.pdpDetailsObserver.observe(this.pdpDetails);
    }

    syncPrice() {
      const livePrice = document.getElementById(`price-${this.sectionId}`);
      if (!livePrice || !this.stickyPriceTarget) return;

      const priceEl = livePrice.querySelector('.price-item--sale') || livePrice.querySelector('.price-item--regular');
      if (priceEl) {
        const clone = priceEl.cloneNode(true);
        clone.querySelectorAll('.mrp-price, .inc-tax').forEach(el => el.remove());
        const priceText = clone.textContent.trim();
        if (priceText) {
          this.stickyPriceTarget.textContent = priceText;
        }
      }
    }

    syncButtonState() {
      const ctaState = this.getActiveCtaState();
      const requiresSize = this.requiresSizeSelection();
      const hasSelectedSize = Boolean(this.getSelectedSizeInput());
      if (!ctaState) return;

      this.stickyButton.dataset.mode = ctaState.mode;

      if (this.buttonsRow && this.soldOutButton) {
        const currentVariant = this.getCurrentVariantData();
        const variantOos = currentVariant && !currentVariant.available;
        const isNotify = ctaState.mode === 'notify';
        const isSoldOut = ctaState.mode === 'sold-out' || isNotify;
        this.buttonsRow.style.display = isSoldOut ? 'none' : '';
        this.soldOutButton.hidden = !isSoldOut;
        if (isSoldOut) {
          const label = isNotify ? ctaState.label : this.dataset.soldOutCta;
          this.setButtonLabel(this.soldOutButton, label || this.dataset.soldOutCta);
          this.soldOutButton.disabled = !isNotify;
          this.soldOutButton.dataset.mode = isNotify ? 'notify' : 'sold-out';
        }
        if (this.infoRow && isSoldOut) {
          this.infoRow.style.display = 'none';
        }
      }

      if (ctaState.mode === 'notify' || ctaState.mode === 'sold-out') {
        this.stickyButton.disabled = false;
      } else if (requiresSize && !hasSelectedSize) {
        this.stickyButton.disabled = false;
      } else {
        this.stickyButton.disabled = ctaState.disabled;
      }

      if (this.modalSubmit) {
        this.modalSubmit.disabled = this.isSubmitting ? true : this.stickyButton.disabled;
      }

      // Fade sticky ATC + Buy Now when variant is OOS
      const currentVariantForFade = this.getCurrentVariantData();
      const variantOosForFade = currentVariantForFade && !currentVariantForFade.available;
      if (variantOosForFade && ctaState.mode === 'cart') {
        this.stickyButton.classList.add('variant-oos');
        if (this.buyNowButton) this.buyNowButton.classList.add('variant-oos');
        if (this.modalSubmit) this.modalSubmit.classList.add('variant-oos');
      } else {
        this.stickyButton.classList.remove('variant-oos');
        if (this.buyNowButton) this.buyNowButton.classList.remove('variant-oos');
        if (this.modalSubmit) this.modalSubmit.classList.remove('variant-oos');
      }

      if (this.buyNowButton) {
        const shouldDisableBuyNow = ctaState.mode === 'sold-out' || ctaState.mode === 'notify' || ctaState.mode === 'variant-unavailable';
        this.buyNowButton.disabled = shouldDisableBuyNow;
      }

      if (this.modalBuyNow) {
        const currentVariant = this.getCurrentVariantData();
        const variantUnavailable = currentVariant && !currentVariant.available;
        const shouldDisableModalBuyNow = ctaState.mode === 'sold-out' || ctaState.mode === 'notify' || ctaState.mode === 'variant-unavailable';
        this.modalBuyNow.disabled = shouldDisableModalBuyNow;
        if (variantUnavailable && !shouldDisableModalBuyNow) {
          this.modalBuyNow.classList.add('variant-oos');
        } else {
          this.modalBuyNow.classList.remove('variant-oos');
        }
      }
    }

    syncVariantTextFromDom() {
      const checkedInputs = Array.from(this.rootSection.querySelectorAll('variant-radios input[type="radio"]:checked, variant-selects select'));
      let text = '';

      checkedInputs.forEach((input) => {
        if (input.tagName === 'SELECT') {
          const selectedOption = input.options[input.selectedIndex];
          if (selectedOption && selectedOption.value && selectedOption.value !== 'Default Title') {
            text = text || selectedOption.value;
          }
          return;
        }

        const fieldset = input.closest('.product-form__input');
        const labelText = fieldset?.querySelector('.form__label')?.textContent?.trim()?.toLowerCase() || '';
        if (labelText.includes('color') || labelText.includes('colour')) return;
        if (input.value) text = text || input.value;
      });

      if (!text) {
        const variantIdInput = this.form.querySelector('input[name="id"]');
        const currentVariant = variantIdInput?.value ? this.getVariantTitleFromJson(variantIdInput.value) : '';
        text = currentVariant || this.variantText.textContent.trim();
      }

      this.updateVariantText(text);
    }

    updateVariantText(rawText) {
      if (!this.variantText) return;
      const cleanText = (rawText || '').replace(/\s*\/\s*/g, ' / ').trim();
      this.variantText.textContent = cleanText && cleanText !== 'Default Title' ? cleanText : this.defaultVariantText;
    }

    handleBuyNowClick() {
      this.flushSync();
      console.log('[DEBUG] Sticky Buy Now clicked, variant-oos:', this.buyNowButton?.classList.contains('variant-oos'), 'disabled:', this.buyNowButton?.disabled);
      if (this.needsVariantSelection()) {
        console.log('[DEBUG] Sticky Buy Now: needs variant selection, jerking');
        this.triggerSizeJerk();
        return;
      }

      const currentVariant = this.getCurrentVariantData();
      console.log('[DEBUG] Sticky Buy Now: currentVariant available:', currentVariant?.available);
      if (currentVariant && !currentVariant.available) {
        console.log('[DEBUG] Sticky Buy Now: variant OOS, jerking');
        this.triggerSizeJerk();
        return;
      }

      if (typeof onBuyNowClickMagic === 'function') {
        onBuyNowClickMagic();
      }
    }

    handleSoldOutClick() {
      this.flushSync();
      if (this.soldOutButton?.dataset.mode !== 'notify') return;
      const ctaState = this.getActiveCtaState();
      if (ctaState?.mode === 'notify' && ctaState.element) {
        ctaState.element.click();
        return;
      }
      const notifyBtn = this.formWrapper?.querySelector('.notify-me-btn');
      if (notifyBtn) {
        notifyBtn.click();
      }
    }

    handleModalBuyNowClick() {
      this.flushSync();
      if (this.isSubmitting) return;

      const selectedSize = this.getSelectedSizeInput();
      if (!selectedSize) {
        this.showModalError(true);
        this.playModalSizeJerk();
        return;
      }

      const currentVariant = this.getCurrentVariantData();
      if (currentVariant && !currentVariant.available) {
        this.playModalSizeJerk();
        return;
      }

      this.showModalError(false);
      this.closeModal();
      this.handleBuyNowClick();
    }

    handleStickyClick() {
      this.flushSync();
      const ctaState = this.getActiveCtaState();
      if (!ctaState) return;

      if (ctaState.mode === 'notify') {
        ctaState.element.click();
        return;
      }

      if (this.needsVariantSelection()) {
        this.triggerSizeJerk();
        return;
      }

      const currentVariant = this.getCurrentVariantData();
      if (currentVariant && !currentVariant.available) {
        if (this.requiresSizeSelection()) {
          this.openModal();
          this.playModalSizeJerk();
        } else {
          this.scrollToMainAtc();
        }
        return;
      }

      if (ctaState.mode === 'variant-unavailable') {
        this.scrollToMainAtc();
        return;
      }

      if (ctaState.mode === 'sold-out') {
        return;
      }

      if (this.isSubmitting) return;

      const realButton = ctaState.element;
      if (!realButton || realButton.disabled) return;

      this.forwardToOriginalAddToCart(realButton);
    }

    needsVariantSelection() {
      if (this.requiresColorSelection() && !this.getSelectedColorInput()) return true;
      if (this.requiresSizeSelection() && !this.getSelectedSizeInput()) return true;
      return false;
    }

    requiresColorSelection() {
      return Boolean(this.getColorFieldset());
    }

    getColorFieldset() {
      const fieldsets = Array.from(this.rootSection.querySelectorAll('fieldset.product-form__input'));
      return fieldsets.find((fieldset) => {
        const labelText = fieldset.querySelector('.form__label')?.textContent?.trim()?.toLowerCase() || '';
        return labelText.includes('color') || labelText.includes('colour');
      }) || null;
    }

    getSelectedColorInput() {
      const colorFieldset = this.getColorFieldset();
      if (!colorFieldset) return null;
      return colorFieldset.querySelector('input[type="radio"]:checked') || null;
    }

    // handleModalSubmit() {
    //   if (this.isSubmitting) return;

    //   const selectedSize = this.getSelectedSizeInput();
    //   if (!selectedSize) {
    //     this.showModalError(true);
    //     this.playModalSizeJerk();
    //     return;
    //   }

    //   this.showModalError(false);
    //   this.closeModal();

    //   const ctaState = this.getActiveCtaState();
    //   if (!ctaState) return;

    //   if (ctaState.mode === 'notify') {
    //     ctaState.element.click();
    //     return;
    //   }

    //   if (ctaState.mode === 'sold-out') {
    //     return;
    //   }

    //   const realButton = ctaState.element;
    //   if (!realButton || realButton.disabled) return;

    //   this.forwardToOriginalAddToCart(realButton);
    // }

    handleModalSubmit() {
      this.flushSync();
      if (this.isSubmitting) return;

      const selectedSize = this.getSelectedSizeInput();
      if (!selectedSize) {
        this.showModalError(true);
        this.playModalSizeJerk();
        return;
      }

      const currentVariant = this.getCurrentVariantData();
      if (currentVariant && !currentVariant.available) {
        this.playModalSizeJerk();
        return;
      }

      const ctaState = this.getActiveCtaState();
      if (!ctaState) return;

      this.showModalError(false);
      this.closeModal();

      if (ctaState.mode === 'notify') {
        ctaState.element.click();
        return;
      }

      const realButton = ctaState.element;
      if (!realButton || realButton.disabled) return;

      this.forwardToOriginalAddToCart(realButton);
    }

    forwardToOriginalAddToCart(realButton) {
      this.isSubmitting = true;
      this.setLoadingState(true);
      this.stickyButton.disabled = true;
      this.stickyButton.setAttribute('aria-disabled', 'true');
      if (this.modalSubmit) {
        this.modalSubmit.disabled = true;
        this.modalSubmit.setAttribute('aria-disabled', 'true');
      }
      realButton.click();
      this.waitForAddToCartCompletion(realButton);
    }

    requiresSizeSelection() {
      return Boolean(this.getSizeFieldset());
    }

    getSizeFieldset() {
      const fieldsets = Array.from(this.rootSection.querySelectorAll('.other-opt-container .product-form__input, fieldset.product-form__input'));
      return fieldsets.find((fieldset) => {
        const labelText = fieldset.querySelector('.form__label')?.textContent?.trim()?.toLowerCase() || '';
        return labelText.includes('size');
      }) || null;
    }

    getSelectedSizeInput() {
      const sizeFieldset = this.getSizeFieldset();
      if (!sizeFieldset) return null;

      return sizeFieldset.querySelector('input[type="radio"]:checked') || null;
    }

    syncOosMessage() {
      const oosMessage = this.rootSection?.querySelector('#oos-message');
      const oosValue = this.rootSection?.querySelector('#oos-value');
      const stickyOosMessage = this.modal?.querySelector('[data-sticky-oos-message]');
      const stickyOosValue = this.modal?.querySelector('[data-sticky-oos-value]');

      if (!oosMessage || !oosValue) {
        if (stickyOosValue) stickyOosValue.textContent = '';
        if (stickyOosMessage) stickyOosMessage.hidden = true;
        return;
      }

      const currentVariant = this.getCurrentVariantData();
      const selectedSize = this.getSelectedSizeInput();

      if (currentVariant && selectedSize?.value && window.inventory_details && window.inventory_details[selectedSize.value]) {
        var inventoryMsg = window.inventory_details[selectedSize.value];
        oosValue.innerHTML = inventoryMsg;
        oosMessage.style.display = 'block';
        if (stickyOosValue) stickyOosValue.innerHTML = inventoryMsg;
        if (stickyOosMessage) stickyOosMessage.hidden = false;
        return;
      }

      if (currentVariant && currentVariant.available === false && selectedSize?.value) {
        oosValue.textContent = 'No Stock Available for UK ' + selectedSize.value;
        oosMessage.style.display = 'block';
        if (stickyOosValue) stickyOosValue.textContent = 'No Stock Available for UK ' + selectedSize.value;
        if (stickyOosMessage) stickyOosMessage.hidden = false;
        return;
      }

      oosValue.textContent = '';
      oosMessage.style.display = 'none';
      if (stickyOosValue) stickyOosValue.textContent = '';
      if (stickyOosMessage) stickyOosMessage.hidden = true;
    }

    syncSizeNote() {
      const note = this.modal?.querySelector('[data-sticky-size-note]');
      const label = this.modal?.querySelector('[data-sticky-size-note-value]');
      if (!note || !label) return;
      // A product-level note is server-rendered for every size, so it never changes here.
      if (note.hasAttribute('data-product-note')) return;

      // The hidden id input is pre-filled with the first available variant, so the note
      // only counts as relevant once a size radio has actually been checked.
      const hasPickedSize = !this.requiresSizeSelection() || Boolean(this.getSelectedSizeInput());
      const currentVariant = hasPickedSize ? this.getCurrentVariantData() : null;
      const notes = window.variant_size_notes || {};
      // Sold out sizes already show the OOS message, so the note stays hidden there.
      const text = currentVariant && currentVariant.available ? notes[currentVariant.id] : null;

      if (text) {
        label.textContent = text;
        note.hidden = false;
      } else {
        label.textContent = '';
        note.hidden = true;
      }
    }

    triggerSizeJerk() {
      if (this.requiresSizeSelection()) {
        this.openModal();
        this.showModalError(false);
        if (typeof window.triggerSizeVariantJerk === 'function') {
          window.triggerSizeVariantJerk(this.rootSection);
        }
      } else {
        this.scrollToMainAtc();
        if (typeof window.triggerSizeVariantJerk === 'function') {
          window.triggerSizeVariantJerk(this.rootSection);
        }
      }
    }

    scrollToMainAtc() {
      var target = this.rootSection?.querySelector('variant-radios') || this.ctaContainer;
      if (!target) return;
      var rect = target.getBoundingClientRect();
      var headerOffset = this.getHeaderOffset();
      var top = rect.top + (window.pageYOffset || 0) - headerOffset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    getHeaderOffset() {
      var selectors = ['.shopify-section-group-header-group', 'sticky-header', '.header-wrapper'];
      var maxBottom = 0;
      selectors.forEach(function(sel) {
        var el = document.querySelector(sel);
        if (!el) return;
        var style = window.getComputedStyle(el);
        if (style.position !== 'fixed' && style.position !== 'sticky') return;
        var r = el.getBoundingClientRect();
        if (r.bottom > 0) maxBottom = Math.max(maxBottom, r.bottom);
      });
      return Math.max(window.innerWidth < 750 ? 110 : 32, Math.round(maxBottom) + 16);
    }

    playModalSizeJerk() {
      const sheet = this.modal?.querySelector('.custom-sticky-atc__sheet');
      if (!sheet) return;

      sheet.classList.remove('is-size-jerking');
      void sheet.offsetWidth;
      sheet.classList.add('is-size-jerking');

      window.clearTimeout(this.sizeJerkTimer);
      this.sizeJerkTimer = window.setTimeout(() => {
        sheet.classList.remove('is-size-jerking');
      }, 450);
    }

    syncModalOptions() {
      if (!this.modalOptions) return;

      const sizeFieldset = this.getSizeFieldset();
      if (!sizeFieldset) {
        this.modalOptions.innerHTML = '';
        return;
      }

      this.cloneSizeGuide(sizeFieldset);

      const inputs = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]'));
      const selectedInput = this.getSelectedSizeInput();
      const fragment = document.createDocumentFragment();

      inputs.forEach((input) => {
        const optionButton = document.createElement('button');
        const label = this.rootSection.querySelector(`label[for="${input.id}"]`);
        const text = label?.querySelector('span:not(.visually-hidden)')?.textContent?.trim() || input.value;
        const isSoldOutOption = input.classList.contains('disabled');

        optionButton.type = 'button';
        optionButton.className = 'custom-sticky-atc__option';
        optionButton.textContent = text;
        optionButton.dataset.inputId = input.id;
        optionButton.dataset.selected = selectedInput && selectedInput.id === input.id ? 'true' : 'false';
        optionButton.dataset.soldOut = isSoldOutOption ? 'true' : 'false';
        optionButton.disabled = input.disabled;
        optionButton.addEventListener('click', () => this.selectSizeOption(input.id));
        fragment.appendChild(optionButton);
      });

      this.modalOptions.replaceChildren(fragment);
      this.syncModalSubmitState();
    }

    cloneSizeGuide(sizeFieldset) {
      if (!this.sizeGuideSlot) return;

      const guideSource = sizeFieldset.closest('.other-opt-container')?.querySelector('.size-chart-container');
      if (!guideSource) {
        this.sizeGuideSlot.innerHTML = '';
        return;
      }

      const clone = guideSource.cloneNode(true);
      this.sizeGuideSlot.replaceChildren(clone);
    }

    selectSizeOption(inputId) {
      const input = this.rootSection.querySelector(`#${CSS.escape(inputId)}`);
      if (!input || input.disabled) return;

      this.updateModalSelectionState(inputId);
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.dispatchEvent(new Event('input', { bubbles: true }));
      window.scrollTo({ top: scrollTop, behavior: 'auto' });
      this.showModalError(false);
      window.setTimeout(() => this.handleDomSync(), 0);
    }

    updateModalSelectionState(inputId) {
      if (!this.modalOptions) return;

      this.modalOptions.querySelectorAll('.custom-sticky-atc__option').forEach((button) => {
        button.dataset.selected = button.dataset.inputId === inputId ? 'true' : 'false';
      });
    }

    syncModalSubmitState() {
      if (!this.modalSubmit) return;
      const ctaState = this.getActiveCtaState();
      if (!ctaState) {
        this.modalSubmit.disabled = true;
        return;
      }

      this.modalSubmit.disabled = this.isSubmitting || ctaState.mode === 'notify';
    }


    openModal() {
      if (!this.modal) return;
      this.syncModalOptions();
      this.modal.hidden = false;
      this.modal.dataset.state = 'open';
      this._scrollY = window.scrollY;
      document.body.classList.add('overflow-hidden');
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${this._scrollY}px`;
      this.showModalError(false);
      this.syncModalSubmitState();

      window.requestAnimationFrame(() => {
        const target = this.modal.querySelector('[data-selected="true"]') || this.modal.querySelector('.custom-sticky-atc__option:not(:disabled)');
        target?.focus();
      });
    }

    closeModal() {
      if (!this.modal) return;
      if (this.modal.hidden || this.modal.dataset.state === 'closing') return;

      this.modal.dataset.state = 'closing';

      window.setTimeout(() => {
        if (!this.modal) return;
        this.modal.hidden = true;
        this.modal.dataset.state = '';
        document.body.classList.remove('overflow-hidden');
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        window.scrollTo(0, this._scrollY || 0);
        this.showModalError(false);
        this.evaluateVisibility();
      }, 400);
    }

    showModalError(shouldShow) {
      if (!this.modalError) return;
      this.modalError.hidden = !shouldShow;
    }

    syncFormError() {
      if (!this.stickyFormErrors?.length) return;

      const sourceMessage = this.originalErrorWrapper?.querySelector('.product-form__error-message')?.textContent?.trim() || '';
      const shouldShow = Boolean(sourceMessage) && this.originalErrorWrapper && !this.originalErrorWrapper.hasAttribute('hidden');

      this.stickyFormErrors.forEach((wrapper) => {
        wrapper.toggleAttribute('hidden', !shouldShow);
        const message = wrapper.querySelector('.product-form__error-message');
        if (message) {
          message.textContent = sourceMessage;
        }
      });
    }

    handleEscape(event) {
      if (event.key === 'Escape' && this.modal && !this.modal.hidden) {
        this.closeModal();
      }
    }

    isTargetOutOfView(rect) {
      if (!rect) return false;
      // scrollPastOnly: only treat the CTA as out of view once it has scrolled ABOVE the
      // viewport, so the bar never shows before the shopper has reached the real button.
      if (this.scrollPastOnly) return rect.bottom <= 0;
      return !(rect.bottom > 0 && rect.top < window.innerHeight);
    }

    getObservedCtaTarget() {
      return this.getActiveCtaState()?.element || this.ctaContainer;
    }

    getActiveCtaState() {
      const customStaticButton = this.formWrapper.querySelector('.custom-static-btn');
      const notifyButton = this.formWrapper.querySelector('.notify-me-btn');
      const stockedOutButton = this.formWrapper.querySelector('button[name="add"].product-form__submit.stocked-out');
      const realButton = this.form.querySelector('button[name="add"].product-form__submit');
      const preorderText = this.formWrapper.querySelector('#preorder-text');

      if (this.isElementVisible(customStaticButton)) {
        return {
          mode: 'variant-unavailable',
          element: customStaticButton,
          label: this.getButtonText(customStaticButton) || this.dataset.defaultCta,
          disabled: false,
        };
      }

      if (this.isElementVisible(notifyButton)) {
        return {
          mode: 'notify',
          element: notifyButton,
          label: this.getButtonText(notifyButton) || this.dataset.notifyCta,
          disabled: false,
        };
      }

      if (this.isElementVisible(stockedOutButton)) {
        return {
          mode: 'sold-out',
          element: stockedOutButton,
          label: this.getButtonText(stockedOutButton) || this.dataset.soldOutCta,
          disabled: false,
        };
      }

      if (realButton) {
        const isPreorder = this.isElementVisible(preorderText) && !this.isElementVisible(realButton);
        return {
          mode: isPreorder ? 'preorder' : 'cart',
          element: realButton,
          label: this.getButtonText(realButton) || this.dataset.defaultCta,
          disabled: realButton.disabled,
        };
      }

      return null;
    }

    isElementVisible(element) {
      if (!element) return false;
      const styles = window.getComputedStyle(element);
      return styles.display !== 'none' && styles.visibility !== 'hidden';
    }

    getButtonText(element) {
      if (!element) return '';
      const label = element.querySelector('span');
      return (label ? label.textContent : element.textContent).trim().replace(/\s+/g, ' ');
    }


    setButtonLabel(button, label) {
      if (!button) return;
      const labelElement = button.querySelector('span');
      if (labelElement) {
        labelElement.textContent = label;
        return;
      }
      button.textContent = label;
    }

    setLoadingState(isLoading) {
      this.toggleButtonLoading(this.stickyButton, isLoading);
      this.toggleButtonLoading(this.modalSubmit, isLoading);
    }

    toggleButtonLoading(button, isLoading) {
      if (!button) return;
      const spinner = button.querySelector('.loading-overlay__spinner');
      if (spinner) {
        spinner.classList.toggle('hidden', !isLoading);
      }
      button.classList.toggle('loading', isLoading);
      button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      button.disabled = isLoading;
      button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
    }

    waitForAddToCartCompletion(realButton) {
      const finishLoading = () => {
        if (!this.isSubmitting) return;
        this.isSubmitting = false;
        this.setLoadingState(false);
        this.handleDomSync();
      };

      const isRealButtonLoading = () => {
        const spinner = realButton?.querySelector('.loading-overlay__spinner');
        const spinnerVisible = spinner && !spinner.classList.contains('hidden');
        const isLoading = realButton?.classList.contains('loading');
        const isAriaDisabled = realButton?.getAttribute('aria-disabled') === 'true';
        return Boolean(spinnerVisible || isLoading || isAriaDisabled);
      };

      if (!isRealButtonLoading()) {
        window.setTimeout(finishLoading, 0);
        return;
      }

      let settledTimer = null;
      const scheduleFinishIfReady = () => {
        if (isRealButtonLoading()) {
          if (settledTimer) {
            window.clearTimeout(settledTimer);
            settledTimer = null;
          }
          return;
        }

        if (settledTimer) return;
        settledTimer = window.setTimeout(() => {
          observer?.disconnect();
          finishLoading();
        }, 80);
      };

      const observer = new MutationObserver(() => {
        scheduleFinishIfReady();
      });

      observer.observe(realButton, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['class', 'aria-disabled'],
      });

      scheduleFinishIfReady();
    }

    evaluateVisibility() {
      const target = this.getObservedCtaTarget();
      if (!target) return;

      const rect = target.getBoundingClientRect();
      this.isCtaOutOfView = this.isTargetOutOfView(rect);

      const barHeight = this.bar?.offsetHeight || 0;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      const viewportBottom = scrollTop + window.innerHeight;
      const documentHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.offsetHeight,
        document.body.clientHeight,
        document.documentElement.clientHeight
      );
      const distanceFromPageBottom = documentHeight - viewportBottom;
      const footerTop = this.getFooterTopPosition();
      const isNearFooterTarget = footerTop !== null && viewportBottom >= (footerTop - barHeight - this.footerHideOffset);
      const isNearPageBottom = distanceFromPageBottom <= (barHeight + this.footerHideDistance);
      this.isFooterVisible = isNearFooterTarget || isNearPageBottom;

      this.updateBarVisibility();
    }

    updateBarVisibility() {
      const isModalOpen = this.modal && !this.modal.hidden;
      this.toggleBar(isModalOpen || (this.isCtaOutOfView && !this.isFooterVisible));
    }

    handleViewportChange() {
      if (this.modal && !this.modal.hidden) return;
      this.evaluateVisibility();
    }

    getFooterTopPosition() {
      if (!this.footerTargets?.length) return null;

      const visibleTargets = this.footerTargets.filter((target) => {
        const styles = window.getComputedStyle(target);
        return styles.display !== 'none' && styles.visibility !== 'hidden';
      });

      if (!visibleTargets.length) return null;

      return visibleTargets.reduce((topPosition, target) => {
        const rect = target.getBoundingClientRect();
        const absoluteTop = rect.top + (window.pageYOffset || document.documentElement.scrollTop || 0);
        return topPosition === null ? absoluteTop : Math.min(topPosition, absoluteTop);
      }, null);
    }

    toggleBar(isVisible) {
      this.bar.dataset.visible = isVisible ? 'true' : 'false';
    }

    getCurrentVariantId() {
      return this.form?.querySelector('input[name="id"]')?.value;
    }

    getCurrentVariantData() {
      const variantId = this.getCurrentVariantId();
      const productJson = this.rootSection?.querySelector('variant-radios script[type="application/json"], variant-selects script[type="application/json"]');
      if (!variantId || !productJson) return null;

      try {
        const variants = JSON.parse(productJson.textContent || '[]');
        return variants.find((variant) => String(variant.id) === String(variantId)) || null;
      } catch (error) {
        return null;
      }
    }

    getVariantTitleFromJson(variantId) {
      const productJson = this.rootSection?.querySelector('variant-radios script[type="application/json"], variant-selects script[type="application/json"]');
      if (!productJson) return '';

      try {
        const variants = JSON.parse(productJson.textContent || '[]');
        const match = variants.find((variant) => String(variant.id) === String(variantId));
        return match?.title || '';
      } catch (error) {
        return '';
      }
    }
  }

  customElements.define('custom-sticky-add-to-cart', CustomStickyAddToCart);
})();
